import { Either, Runtime } from "fp";
import React from "react";

import * as Context from "~/Context";
import { Graph, gql } from "~/Graph";
import { ID } from "~/ID";
import * as GenericLink from "~/Link";
import { Order } from "~/Order";
import { Search } from "~/Search";

export type Asset = {
  readonly asset: ID & {
    readonly name?: string;
  };
};
export namespace Asset {
  export const name = ({ asset: { name, id } }: Asset): string => name && name.length > 0 ? name : id;

  export const order = () =>
    Order.fromCompare<Asset | undefined>((a, b) =>
      Order.string().compare(a ? name(a) : "N/A", b ? name(b) : "N/A")
    );

  export const { equals, compare } = order();

  export const toSearch: Search.From<Asset> = ({ asset: { id, name } }) =>
    Search.join(Search.string(id), name ? Search.string(name) : Search.empty());

  export const useHREF = ({ asset: { id } }: Asset) =>
    `${Context.useBaseURL()}/#/assets/all/asset/${id}/history`;

  export type Fragment = Graph.Generated.AssetFragment;
  export namespace Fragment {
    export const document = () => gql`
      fragment Asset on Asset {
        id
        customName
      }
    `;

    export const decode = (original: Fragment): Asset => ({
      asset: {
        ...ID.from(original.id),
        name: original.customName ?? undefined,
      },
    });
  }

  export type Result = Either.ErrorOr<Asset>;
  export type OptionalResult = Either.ErrorOr<Asset | undefined>;
  export namespace Result {
    export type Fragment = Graph.Generated.Asset__ResultFragment;
    export namespace Fragment {
      export const document = () => gql`
        ${Asset.Fragment.document()}
        ${Graph.Errors.Fragment.document()}

        fragment Asset__Result on Asset__Result {
          ... on Asset {
            ...Asset
          }

          ... on Error {
            ...Error
          }
        }
      `;

      export const decode = (encoded: Fragment): Result =>
        "message" in encoded
          ? Either.left(Error(encoded.message))
          : Either.right(Asset.Fragment.decode(encoded));
    }
  }

  export namespace Response {
    export const runtime = () =>
      Runtime.strict({
        asset_id: Runtime.number(),
        custom_name: Runtime.nullable(Runtime.string()),
      });

    export const decode = (
      response: Runtime.ToType<typeof runtime>
    ): Asset => ({
      asset: {
        id: `${response.asset_id}`,
        name: response.custom_name ?? undefined,
      },
    });
  }

  export const Link = ({
    asset,
    noLink = false,
    children = Asset.name({ asset }),
  }: React.PropsWithChildren<Asset & { readonly noLink?: boolean }>) =>
    pipe(useHREF({ asset }), (href) =>
      noLink ? (
        <>{children}</>
      ) : (
        <GenericLink.Component target="_blank" href={href}>
          {children}
        </GenericLink.Component>
      )
    );
}

export type Assets = readonly Asset[];
